'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import Container from '@src/components/Shared/Container';
import InlineSVG from '@src/components/Shared/InlineSVG';
import ValdHubSlider from '@src/components/Shared/ValdHubSlider';
import NextImage from '@src/components/Shared/NextImage';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';

import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import CallToAction from '@src/components/Sections/CallToAction';
import HotSpots from '@src/components/Sections/HotSpots';
import SvgTextPrimary from '@src/components/Sections/SvgTextPrimary';
import Testimonials from '@src/components/Sections/Testimonials';

import usePreviewContent from '@src/hooks/usePreviewContent';

import isEmpty from '@src/utils/isEmpty';

import constructURL from '@src/utils/constructURL';

import { ValdHubPageClientPropTypes } from './ValdHubPage.types';

const ValdHubPageClient: React.FC<ValdHubPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/products/valdhub`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  return (
    <>
      <div
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        className='valdHubhero bg-[radial-gradient(53.61%_117.15%_at_50%_50%,#F4AC40_0%,#f16e00_74.11%)] py-[8rem]'
      >
        <Container className='pb-[8rem] md:pb-[17rem] xl:pb-[20rem]'>
          <InlineSVG
            className='mx-auto w-full max-w-[45.6rem]'
            src={page?.hero?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop', 'tablet', 'mobile']}
          />
          <ValdHubSlider
            title={page?.hero?.items?.[0]?.title}
            preTitle={page?.hero?.items?.[0]?.preTitle}
            bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
          />
        </Container>
      </div>

      <Container className='mb-[0]!' background='bg-background-tertiary'>
        <NextImage
          className='top-[-17rem] mx-auto w-[70%]! xl:top-[-30rem]'
          objectFit='cover'
          priority
          src={page?.hero?.items?.[0]?.mediaSecondary?.url}
          alt={page?.hero?.items?.[0]?.mediaSecondary?.description ?? 'VALD Image'}
          aspectRatio={
            page?.hero?.items?.[0]?.mediaSecondary?.width /
            page?.hero?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.hero?.items?.[0]?.mediaSecondary?.width}
        />
        <div className='mx-auto mt-[-12rem] flex justify-center xl:mt-[-25rem]'>
          <ul className='mb-[6rem] flex w-full flex-wrap justify-around gap-[1rem] gap-y-[3rem] text-center md:w-[60%] xl:flex-nowrap'>
            {page?.hero?.items?.[0]?.extraAssetsCollection?.items?.map((asset) => {
              if (isEmpty({ value: asset })) return null;
              return (
                <li
                  key={`${asset?.sys?.id}-svg-icons`}
                  className='flex w-[21%] flex-col items-center justify-start'
                >
                  <InlineSVG
                    className='flex min-h-[3rem] items-center [&>svg]:h-[2.4rem] [&>svg]:w-[2.4rem]'
                    src={asset?.media?.url}
                    renderAt={['desktop', 'tablet', 'mobile']}
                  />
                  <p className='mt-[1.6rem] text-[1.6rem] font-medium leading-[1.7rem]'>
                    {asset?.title}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </Container>

      <Container
        id={page?.centralized?.items?.[0]?.scrollTo?.slice(1)}
        className='py-[5rem] md:flex md:flex-col'
        background='bg-background-tertiary'
      >
        <SectionHeader
          className='justify-center md:mb-[3.4rem] lg:pb-[unset]'
          pretitle={page?.centralized?.items?.[0]?.preTitle}
          title={page?.centralized?.items?.[0]?.title}
          titleClassName='max-w-[61.6rem] [&>span]:drop-shadow-xs text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='  lg:max-w-[78.2rem]'
              bodyCopy={page?.centralized?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.centralized?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
      </Container>

      <ImageTextSecondary
        reversed
        id={page?.fullFeatured?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.fullFeatured?.items?.[0]?.preTitle}
        title={page?.fullFeatured?.items?.[0]?.title}
        imgURL={page?.fullFeatured?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.fullFeatured?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.fullFeatured?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.fullFeatured?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        background='lg:bg-background-tertiary'
        className='lg:overflow-visible!'
        id={page?.efficientAndAccurate?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.efficientAndAccurate?.items?.[0]?.preTitle}
        title={page?.efficientAndAccurate?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs'
        imgURL={page?.efficientAndAccurate?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.efficientAndAccurate?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.efficientAndAccurate?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Container
        id={page?.customizable?.items?.[0]?.scrollTo?.slice(1)}
        className={`flex flex-col items-stretch md:gap-[2rem] lg:flex-row 2xl:mb-[0]!`}
      >
        {/* Mobile image */}
        <NextImage
          className='-order-1 mb-[2.4rem] mr-[-2.8rem] w-[39.5rem] lg:hidden'
          src={page?.customizable?.items?.[0]?.mediaPrimary?.url}
          alt={page?.customizable?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          width={page?.customizable?.items?.[0]?.mediaPrimary?.width}
          aspectRatio={
            page?.customizable?.items?.[0]?.mediaPrimary?.width /
            page?.customizable?.items?.[0]?.mediaPrimary?.height
          }
        />

        {/* Tablet image */}
        <NextImage
          className='order-1 mr-[-2.8rem] hidden lg:block xl:mr-[-12.8rem] 2xl:hidden '
          src={page?.customizable?.items?.[0]?.mediaSecondary?.url}
          alt={page?.customizable?.items?.[0]?.mediaSecondary?.description ?? 'VALD Image'}
          width={page?.customizable?.items?.[0]?.mediaPrimary?.width}
          aspectRatio={
            page?.customizable?.items?.[0]?.mediaPrimary?.width /
            page?.customizable?.items?.[0]?.mediaPrimary?.height
          }
        />

        {/* Desktop image */}
        <NextImage
          className='xl:mt-0 order-1 mr-[-2.8rem] mt-[4rem] hidden w-[39.5rem] md:w-[65.8rem] lg:w-[81.8rem] xl:mr-[-12.8rem] 2xl:block'
          src={page?.customizable?.items?.[0]?.mediaTertiary?.url}
          alt={page?.customizable?.items?.[0]?.mediaTertiary?.description ?? 'VALD Image'}
          width={page?.customizable?.items?.[0]?.mediaPrimary?.width}
          aspectRatio={
            page?.customizable?.items?.[0]?.mediaPrimary?.width /
            page?.customizable?.items?.[0]?.mediaPrimary?.height
          }
        />

        <SectionHeader
          className='lg:justify-center xl:w-10/12 2xl:w-7/12 2xl:justify-start'
          titleClassName='text-[2.8rem] xl:text-[4.8rem] leading-[3.2rem] xl:leading-[5rem] font-light 2xl:max-w-[58.8rem] mb-[2.4rem]'
          pretitle={page?.customizable?.items?.[0]?.preTitle}
          title={page?.customizable?.items?.[0]?.title}
          align='left'
        >
          {
            <>
              <RenderBodyCopy
                className=' 2xl:max-w-[78.2rem]'
                bodyCopy={page?.customizable?.items?.[0]?.bodyCopy?.json?.content}
              />
              <RenderButtons items={page?.customizable?.items?.[0]?.buttonCollection?.items} />
            </>
          }
        </SectionHeader>
      </Container>

      <ImageTextSecondary
        background='lg:bg-background-tertiary'
        className='pt-[6rem] lg:mt-[-5rem] lg:overflow-visible!'
        id={page?.printableReports?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.printableReports?.items?.[0]?.preTitle}
        title={page?.printableReports?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs'
        imgURL={page?.printableReports?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.printableReports?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.printableReports?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Container id={page?.normativeData?.items?.[0]?.scrollTo?.slice(1)} className='px-[0]'>
        <SectionHeader
          className='mb-[3.2rem] md:px-[6.4rem] lg:mb-[6.4rem]'
          preTitleClassName='mb-[1.6rem] w-full md:text-center'
          title={page?.normativeData?.items?.[0]?.title}
          pretitle={page?.normativeData?.items?.[0]?.preTitle}
          titleClassName='w-full md:text-center mb-[2.4rem] text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] 2xl:max-w-[118rem] lg:text-[4.8rem] lg:leading-[5rem]'
          childrenClassName='relative'
          align='center'
        >
          <RenderBodyCopy
            className='text-left md:mx-auto md:max-w-[60.7rem] md:text-center 2xl:max-w-[78.2rem]'
            bodyCopy={page?.normativeData?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='md:justify-center'
            items={page?.normativeData?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <HotSpots
          className='mb-[8rem] p-[0]! md:mb-[14rem] 3xl:[&_figure]:left-[5%] 3xl:[&_figure]:w-[90%]'
          data={data?.page?.normativeData?.items?.[0]}
        />
      </Container>

      <ImageTextSecondary
        reversed
        background='bg-background-tertiary'
        className='overflow-visible! py-[6rem]'
        id={page?.toolPermissions?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.toolPermissions?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs'
        imgURL={page?.toolPermissions?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.toolPermissions?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.toolPermissions?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      {/* hide section */}
      <Container
        id={page?.thirdPartyIntegrations?.items?.[0]?.scrollTo?.slice(1)}
        className='locale-en-ca:hidden!'
      >
        <SectionHeader
          className='mb-[3.2rem] md:px-[6.4rem]'
          preTitleClassName='mb-[1.6rem]'
          pretitle={page?.thirdPartyIntegrations?.items?.[0]?.preTitle}
          title={page?.thirdPartyIntegrations?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[7.2rem] leading-[3rem] xl:leading-[7.2rem] font-light mb-[3.5rem] max-w-[77.5rem] '
          childrenClassName='relative'
          align='center'
        >
          <RenderBodyCopy
            className='mx-auto max-w-[51.7rem] text-left md:text-center md:text-[1.8rem] 2xl:max-w-[78.2rem]'
            bodyCopy={page?.thirdPartyIntegrations?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='justify-center'
            items={page?.thirdPartyIntegrations?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <NextImage
          className='mx-auto max-w-[100rem]'
          src={page?.thirdPartyIntegrations?.items?.[0]?.mediaPrimary?.url}
          alt={page?.thirdPartyIntegrations?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          width={page?.thirdPartyIntegrations?.items?.[0]?.mediaPrimary?.width}
          aspectRatio={
            page?.thirdPartyIntegrations?.items?.[0]?.mediaPrimary?.width /
            page?.thirdPartyIntegrations?.items?.[0]?.mediaPrimary?.height
          }
        />
      </Container>

      <Container className='w-full'>
        <NextImage
          className='hidden sm:hidden md:block lg:block xl:block'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaPrimary?.width}
        />
        <NextImage
          className='block sm:block md:hidden lg:hidden xl:hidden'
          entryId={page?.securityPrivacyPolicy?.items?.[0]?.sys?.id}
          src={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.url}
          alt={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.description}
          aspectRatio={
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width /
            page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.height
          }
          width={page?.securityPrivacyPolicy?.items?.[0]?.mediaSecondary?.width}
        />
      </Container>

      <SvgTextPrimary
        reversed
        id={page?.systems?.items?.[0]?.scrollTo?.slice(1)}
        className='overflow-visible! py-[6rem] lg:py-[9rem]'
        background='bg-background-tertiary'
        pretitle={page?.systems?.items?.[0]?.preTitle}
        title={page?.systems?.items?.[0]?.title}
        titleClassName='max-w-[50rem] [&>span]:drop-shadow-xs'
        svgURL={page?.systems?.items?.[0]?.mediaPrimary?.url}
        svgClassName='flex items-center'
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.systems?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={data.page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default ValdHubPageClient;
